import React from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { enGB } from 'date-fns/locale';
import type { Locale } from 'date-fns';

const LEFT_RIGHT_ARROW_SIZE = 24;
const DOWN_ARROW_SIZE = 16;

// ─── Shared grid-state types ─────────────────────────────────────────────────

export interface YearGridState  { year: number; month: number; monthIdx: 0 | 1; top: number; left: number }
export interface MonthGridState { month: number; year: number; panelIdx: 0 | 1; top: number; left: number }

export type ChangeShownDate = (
    value: Date | number | string,
    mode?: 'set' | 'setYear' | 'setMonth' | 'monthOffset',
) => void;

// ─── Props ────────────────────────────────────────────────────────────────────

interface CalendarNavigatorProps {
    panelIdx:        0 | 1;
    focusedDate:     Date;
    changeShownDate: ChangeShownDate;
    locale?:         Locale;
    yearGrid:        YearGridState  | null;
    monthGrid:       MonthGridState | null;
    setYearGrid:     (grid: YearGridState  | null) => void;
    setMonthGrid:    (grid: MonthGridState | null) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CalendarNavigator: React.FC<CalendarNavigatorProps> = ({
    panelIdx, focusedDate, changeShownDate, locale,
    yearGrid, monthGrid, setYearGrid, setMonthGrid,
}) => {
    const loc         = locale ?? enGB;
    const monthName   = loc.localize!.month(focusedDate.getMonth(), { width: 'abbreviated' });
    const isMonthOpen = monthGrid?.panelIdx === panelIdx;
    const isYearOpen  = yearGrid?.monthIdx  === panelIdx;

    return (
        <div className="rdrMonthAndYearWrapper"
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}>
            <div className="rdrMonthAndYearPickers">
                <div className="rdrMonthPicker">
                    <button type="button" className="rdr-picker-btn"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (monthGrid?.panelIdx === panelIdx) { setMonthGrid(null); return; }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setYearGrid(null);
                            setMonthGrid({ month: focusedDate.getMonth(), year: focusedDate.getFullYear(), panelIdx, top: rect.bottom + 4, left: rect.left });
                        }}>
                        {monthName}
                        <ChevronDown size={DOWN_ARROW_SIZE} className={`rdr-picker-caret${isMonthOpen ? ' rdr-picker-caret--open' : ''}`} />
                    </button>
                </div>
                <div className="rdrYearPicker">
                    <button type="button" className="rdr-picker-btn"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (yearGrid?.monthIdx === panelIdx) { setYearGrid(null); return; }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMonthGrid(null);
                            setYearGrid({ year: focusedDate.getFullYear(), month: focusedDate.getMonth(), monthIdx: panelIdx, top: rect.bottom + 4, left: rect.left });
                        }}>
                        {focusedDate.getFullYear()}
                        <ChevronDown size={DOWN_ARROW_SIZE} className={`rdr-picker-caret${isYearOpen ? ' rdr-picker-caret--open' : ''}`} />
                    </button>
                </div>
            </div>
            <button type="button" className="rdrNextPrevButton rdrPprevButton"
                onClick={() => changeShownDate(-1, 'monthOffset')} aria-label="Previous month">
                <ChevronLeft size={LEFT_RIGHT_ARROW_SIZE} />
            </button>
            <button type="button" className="rdrNextPrevButton rdrNextButton"
                onClick={() => changeShownDate(1, 'monthOffset')} aria-label="Next month">
                <ChevronRight size={LEFT_RIGHT_ARROW_SIZE} />
            </button>
        </div>
    );
};

export default CalendarNavigator;

